import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Skeleton,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import SpeedIcon from '@mui/icons-material/Speed';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { reportsApi, ReportTemplate } from '../services/reportsApi';
import { schedulingApi } from '../services/schedulingApi';
import { exportsApi } from '../services/exportsApi';

const Reports: React.FC = () => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'public' | 'private'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; template: ReportTemplate } | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      loadTemplates();
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchTerm, filterType]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params: any = {
        search: searchTerm || undefined,
      };

      if (filterType === 'public') {
        params.is_public = true;
      } else if (filterType === 'private') {
        params.is_public = false;
      }

      const data = await reportsApi.listTemplates(params);
      setTemplates(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (template: ReportTemplate) => {
    try {
      await reportsApi.duplicateTemplate(template.id);
      loadTemplates();
      setMenuAnchor(null);
    } catch (error) {
      console.error('Error duplicating template:', error);
    }
  };

  const handleDelete = async (template: ReportTemplate) => {
    if (window.confirm(`Deseja realmente excluir o template "${template.name}"?`)) {
      try {
        await reportsApi.deleteTemplate(template.id);
        loadTemplates();
        setMenuAnchor(null);
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const handleViewReport = (template: ReportTemplate) => {
    setSelectedTemplate(template);
    setShowViewModal(true);
    setMenuAnchor(null);
  };

  const handleExportReport = async (template: ReportTemplate, format: 'json' | 'csv' | 'excel' | 'pdf') => {
    try {
      setExporting(format);
      if (format === 'json') {
        const data = await exportsApi.exportAsJSON({
          template_id: template.id,
        });
        console.log('Report data:', data);
      } else {
        await exportsApi.exportReport(
          { template_id: template.id },
          format
        );
      }
    } catch (error) {
      console.error('Error exporting report:', error);
    } finally {
      setExporting(null);
    }
  };

  const handleCreateSchedule = async (template: ReportTemplate) => {
    try {
      const schedule = {
        template: template.id,
        name: `${template.name} - Agendado`,
        schedule: 'daily' as const,
        run_time: '09:00',
        recipients: [],
        active: true,
      };

      await schedulingApi.createSchedule(schedule);
      alert('Relatório agendado com sucesso!');
      setMenuAnchor(null);
    } catch (error) {
      console.error('Error creating schedule:', error);
      alert('Erro ao agendar relatório. Tente novamente.');
    }
  };

  const getReportTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      table: <TableChartIcon />,
      chart: <BarChartIcon />,
      kpi: <SpeedIcon />,
      mixed: <DashboardIcon />,
    };
    return icons[type] || <DescriptionIcon />;
  };

  const getReportTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      table: 'Tabela',
      chart: 'Gráfico',
      kpi: 'KPI',
      mixed: 'Misto',
    };
    return labels[type] || type;
  };

  const getReportTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      table: '#2196f3',
      chart: '#4caf50',
      kpi: '#ff9800',
      mixed: '#9c27b0',
    };
    return colors[type] || '#757575';
  };

  // Stats
  const stats = {
    total: templates.length,
    public: templates.filter(t => t.is_public).length,
    private: templates.filter(t => !t.is_public).length,
    active: templates.filter(t => t.is_active).length,
  };

  return (
    <Box sx={{ minHeight: '100%', overflow: 'auto', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: 'var(--text-primary)' }}>
              Relatórios
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Tooltip title="Atualizar">
                <IconButton
                  onClick={loadTemplates}
                  disabled={loading}
                  sx={{
                    background: 'var(--glass-bg)',
                    backdropFilter: 'var(--glass-blur)',
                    border: '1px solid var(--glass-border)',
                    '&:hover': { background: 'var(--glass-bg-strong)' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
                  boxShadow: 'var(--shadow-lg)',
                  '&:hover': { background: 'linear-gradient(135deg, var(--color-primary-700), var(--color-primary-800))' }
                }}
              >
                Novo Relatório
              </Button>
            </Box>
          </Box>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
            Gerencie e visualize seus relatórios customizados
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-glass)'
            }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                Total de Relatórios
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'var(--color-primary-600)' }}>
                {stats.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-glass)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PublicIcon sx={{ fontSize: 18, color: 'var(--color-success-600)' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Públicos
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'var(--color-success-600)' }}>
                {stats.public}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-glass)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LockIcon sx={{ fontSize: 18, color: 'var(--color-warning-600)' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Privados
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'var(--color-warning-600)' }}>
                {stats.private}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-glass)'
            }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                Ativos
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'var(--color-info-600)' }}>
                {stats.active}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Toolbar */}
        <Paper sx={{
          p: 2,
          mb: 3,
          borderRadius: 'var(--radius-xl)',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--glass-border)',
          boxShadow: 'var(--shadow-glass)'
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {/* Search */}
            <TextField
              placeholder="Buscar relatórios..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  borderRadius: 'var(--radius-lg)',
                  '& fieldset': { borderColor: 'var(--glass-border)' },
                  '&:hover fieldset': { borderColor: 'var(--color-primary-300)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--color-primary-500)' }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Filter */}
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={(_, value) => value && setFilterType(value)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2,
                  border: '1px solid var(--glass-border)',
                  '&.Mui-selected': {
                    background: 'var(--color-primary-100)',
                    color: 'var(--color-primary-700)',
                    '&:hover': { background: 'var(--color-primary-200)' }
                  }
                }
              }}
            >
              <ToggleButton value="all">
                Todos
              </ToggleButton>
              <ToggleButton value="public">
                Públicos
              </ToggleButton>
              <ToggleButton value="private">
                Privados
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} lg={4} key={i}>
                <Paper sx={{
                  p: 3,
                  borderRadius: 'var(--radius-xl)',
                  background: 'var(--glass-bg-strong)',
                  backdropFilter: 'var(--glass-blur)',
                  border: '1px solid var(--glass-border)'
                }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Skeleton variant="rounded" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="70%" height={24} />
                      <Skeleton width="40%" height={20} />
                    </Box>
                  </Box>
                  <Skeleton width="100%" height={40} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Skeleton variant="rounded" width={80} height={32} />
                    <Skeleton variant="rounded" width={80} height={32} />
                    <Skeleton variant="rounded" width={80} height={32} />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : templates.length === 0 ? (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 'var(--radius-2xl)',
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-glass)'
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'var(--glass-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 40, color: 'var(--text-muted)' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: 'var(--text-primary)' }}>
              Nenhum relatório encontrado
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
              {searchTerm ? 'Tente buscar com outro termo' : 'Crie seu primeiro relatório customizado'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
                  '&:hover': { background: 'linear-gradient(135deg, var(--color-primary-700), var(--color-primary-800))' }
                }}
              >
                Criar Relatório
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} lg={4} key={template.id}>
                <Card
                  sx={{
                    borderRadius: 'var(--radius-xl)',
                    background: 'var(--glass-bg-strong)',
                    backdropFilter: 'var(--glass-blur)',
                    border: '1px solid var(--glass-border)',
                    boxShadow: 'var(--shadow-glass)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 'var(--shadow-xl)',
                      transform: 'translateY(-2px)',
                      borderColor: 'var(--color-primary-300)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: `${getReportTypeColor(template.report_type)}15`,
                          color: getReportTypeColor(template.report_type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {getReportTypeIcon(template.report_type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {template.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getReportTypeLabel(template.report_type)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              bgcolor: `${getReportTypeColor(template.report_type)}15`,
                              color: getReportTypeColor(template.report_type),
                            }}
                          />
                          {template.is_public ? (
                            <Chip
                              icon={<PublicIcon sx={{ fontSize: 14 }} />}
                              label="Público"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                          ) : (
                            <Chip
                              icon={<LockIcon sx={{ fontSize: 14 }} />}
                              label="Privado"
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => setMenuAnchor({ el: e.currentTarget, template })}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    {/* Description */}
                    {template.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {template.description}
                      </Typography>
                    )}

                    {/* Info */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {template.metrics?.length || 0} métricas
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {template.filters?.length || 0} filtros
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewReport(template)}
                        sx={{ flex: 1, textTransform: 'none' }}
                      >
                        Visualizar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ScheduleIcon />}
                        onClick={() => handleCreateSchedule(template)}
                        sx={{ flex: 1, textTransform: 'none' }}
                      >
                        Agendar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor?.el}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={() => menuAnchor && handleViewReport(menuAnchor.template)}>
            <VisibilityIcon sx={{ mr: 1.5, fontSize: 20 }} />
            Visualizar
          </MenuItem>
          <MenuItem onClick={() => menuAnchor && handleDuplicate(menuAnchor.template)}>
            <ContentCopyIcon sx={{ mr: 1.5, fontSize: 20 }} />
            Duplicar
          </MenuItem>
          <MenuItem onClick={() => menuAnchor && handleCreateSchedule(menuAnchor.template)}>
            <ScheduleIcon sx={{ mr: 1.5, fontSize: 20 }} />
            Agendar
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => menuAnchor && handleDelete(menuAnchor.template)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} />
            Excluir
          </MenuItem>
        </Menu>

        {/* View Modal */}
        <Dialog
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          maxWidth="sm"
          fullWidth
        >
          {selectedTemplate && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        bgcolor: `${getReportTypeColor(selectedTemplate.report_type)}15`,
                        color: getReportTypeColor(selectedTemplate.report_type),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getReportTypeIcon(selectedTemplate.report_type)}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {selectedTemplate.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getReportTypeLabel(selectedTemplate.report_type)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={() => setShowViewModal(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                {selectedTemplate.description && (
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {selectedTemplate.description}
                  </Typography>
                )}

                {/* Export Options */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Exportar Relatório
                  </Typography>
                  <Grid container spacing={1.5}>
                    {(['json', 'csv', 'excel', 'pdf'] as const).map((format) => (
                      <Grid item xs={3} key={format}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => handleExportReport(selectedTemplate, format)}
                          disabled={exporting === format}
                          sx={{
                            py: 1.5,
                            flexDirection: 'column',
                            gap: 0.5,
                            textTransform: 'none',
                          }}
                        >
                          {exporting === format ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon />
                          )}
                          <Typography variant="caption" fontWeight={500}>
                            {format.toUpperCase()}
                          </Typography>
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Details */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Detalhes
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Visibilidade
                      </Typography>
                      <Typography fontWeight={500}>
                        {selectedTemplate.is_public ? 'Público' : 'Privado'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Typography fontWeight={500}>
                        {selectedTemplate.is_active ? 'Ativo' : 'Inativo'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Métricas
                      </Typography>
                      <Typography fontWeight={500}>
                        {selectedTemplate.metrics?.length || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Filtros
                      </Typography>
                      <Typography fontWeight={500}>
                        {selectedTemplate.filters?.length || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Criado por
                      </Typography>
                      <Typography fontWeight={500}>
                        {selectedTemplate.created_by_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(selectedTemplate.created_at).toLocaleString('pt-BR')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setShowViewModal(false)}>
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ScheduleIcon />}
                  onClick={() => {
                    handleCreateSchedule(selectedTemplate);
                    setShowViewModal(false);
                  }}
                >
                  Agendar Relatório
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Create Modal */}
        <Dialog
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Criar Relatório</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 1 }}>
              A funcionalidade de criação de relatórios customizados será implementada em breve.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateModal(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Reports;
